/**
 * Image compression and optimization utilities for file uploads.
 * Uses browser-image-compression library to compress/resize images client-side.
 *
 * @see https://github.com/Donaldcwl/browser-image-compression
 * @see https://docs.anthropic.com/en/docs/build-with-claude/vision (image size limits)
 */

import imageCompression from "browser-image-compression";
import { FILE_SIZE_LIMITS, formatFileSize } from "@/constants";

/**
 * Anthropic API image size limits for optimal performance.
 * Images larger than these dimensions will be resized server-side, increasing latency.
 *
 * @see https://docs.anthropic.com/en/docs/build-with-claude/vision
 */
const ANTHROPIC_IMAGE_CONFIG = {
  /** Maximum dimension (width or height) for optimal API performance */
  MAX_DIMENSION: 1568,
  /** Minimum dimension to maintain image quality */
  MIN_DIMENSION: 200,
  /** Maximum file size in MB */
  MAX_FILE_SIZE_MB: 5,
  /** JPEG quality for resized images (0-1) */
  QUALITY: 0.9,
} as const;

/**
 * Compression strategy based on file size
 */
const COMPRESSION_CONFIG = {
  /** Aggressive compression for files > 5 MB */
  aggressive: {
    maxSizeMB: 3,
    maxWidthOrHeight: 2000,
    quality: 0.8,
    useWebWorker: true,
    preserveExif: false, // Remove EXIF data to reduce size
  },
  /** Moderate compression for files 3-5 MB */
  moderate: {
    maxSizeMB: 2.5,
    maxWidthOrHeight: 2500,
    quality: 0.85,
    useWebWorker: true,
    preserveExif: false,
  },
  /** Light compression for files 2-3 MB */
  light: {
    maxSizeMB: 2,
    maxWidthOrHeight: 3000,
    quality: 0.9,
    useWebWorker: true,
    preserveExif: false,
  },
} as const;

/**
 * Progress callback for compression
 */
export type CompressionProgressCallback = (progress: number) => void;

/**
 * Result of compression operation
 */
export interface CompressionResult {
  /** Compressed file */
  file: File;
  /** Whether compression was applied */
  wasCompressed: boolean;
  /** Original file size in bytes */
  originalSize: number;
  /** Final file size in bytes */
  finalSize: number;
  /** Compression strategy used (if any) */
  strategy?: "aggressive" | "moderate" | "light";
}

/**
 * Determine which compression strategy to use based on file size
 */
function getCompressionStrategy(
  fileSizeBytes: number,
): keyof typeof COMPRESSION_CONFIG | null {
  const sizeMB = fileSizeBytes / (1024 * 1024);

  if (sizeMB > 5) {
    return "aggressive";
  } else if (sizeMB > 3) {
    return "moderate";
  } else if (sizeMB > 2) {
    return "light";
  }

  // Files under 2 MB don't need compression
  return null;
}

/**
 * Compress an image file if it exceeds size thresholds.
 * Small files are returned unchanged for optimal performance.
 *
 * @param file - Image file to compress
 * @param onProgress - Optional progress callback (0-100)
 * @returns Compression result with compressed file
 *
 * @example
 * ```typescript
 * const result = await compressImageIfNeeded(file, (progress) => {
 *   console.log(`Compressing: ${progress}%`);
 * });
 *
 * if (result.wasCompressed) {
 *   console.log(`Reduced from ${formatFileSize(result.originalSize)} to ${formatFileSize(result.finalSize)}`);
 * }
 * ```
 */
export async function compressImageIfNeeded(
  file: File,
  onProgress?: CompressionProgressCallback,
): Promise<CompressionResult> {
  const originalSize = file.size;

  // Determine if compression is needed
  const strategy = getCompressionStrategy(originalSize);

  if (!strategy) {
    // File is small enough, no compression needed
    return {
      file,
      wasCompressed: false,
      originalSize,
      finalSize: originalSize,
    };
  }

  try {
    // Apply compression with selected strategy
    const options = COMPRESSION_CONFIG[strategy];
    const compressedFile = await imageCompression(file, {
      ...options,
      onProgress: onProgress
        ? (progress: number) => onProgress(Math.round(progress))
        : undefined,
    });

    const finalSize = compressedFile.size;

    // Verify compression was successful
    if (finalSize > FILE_SIZE_LIMITS.IMAGE_MAX_SIZE) {
      throw new Error(
        `Compressed image still exceeds ${formatFileSize(FILE_SIZE_LIMITS.IMAGE_MAX_SIZE)} limit (${formatFileSize(finalSize)})`,
      );
    }

    return {
      file: compressedFile,
      wasCompressed: true,
      originalSize,
      finalSize,
      strategy,
    };
  } catch (error) {
    // If compression fails, throw error with details
    const errorMessage =
      error instanceof Error ? error.message : "Unknown compression error";
    throw new Error(
      `Failed to compress image: ${errorMessage}. Original size: ${formatFileSize(originalSize)}`,
      { cause: error },
    );
  }
}

/**
 * Check if a file type supports compression
 */
export function isCompressibleImage(mimeType: string): boolean {
  // JPEG, PNG, WebP are compressible
  // GIF is usually already compressed and may lose quality
  return (
    mimeType === "image/jpeg" ||
    mimeType === "image/png" ||
    mimeType === "image/webp"
  );
}

/**
 * Result of Anthropic optimization operation
 */
export interface OptimizationResult {
  /** Optimized file */
  file: File;
  /** Whether optimization was applied */
  wasOptimized: boolean;
  /** Original file size in bytes */
  originalSize: number;
  /** Final file size in bytes */
  finalSize: number;
  /** Original dimensions */
  originalDimensions?: { width: number; height: number };
  /** Final dimensions */
  finalDimensions?: { width: number; height: number };
}

/**
 * Get image dimensions from a File object
 */
async function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image for dimension check"));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Check if an image needs optimization for Anthropic API.
 * Returns true if dimensions exceed 1568px or file size exceeds 5MB.
 */
async function needsOptimization(file: File): Promise<boolean> {
  // Check file size first (quick check)
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > ANTHROPIC_IMAGE_CONFIG.MAX_FILE_SIZE_MB) {
    return true;
  }

  // Check dimensions
  try {
    const dimensions = await getImageDimensions(file);
    const maxDimension = Math.max(dimensions.width, dimensions.height);
    return maxDimension > ANTHROPIC_IMAGE_CONFIG.MAX_DIMENSION;
  } catch {
    // If we can't check dimensions, optimize to be safe
    return true;
  }
}

/**
 * Optimize an image for the Anthropic API.
 *
 * Resizes images to max 1568px on the longest edge and compresses to max 5MB.
 * This ensures optimal performance and reduces token usage.
 *
 * Images that are already within limits are returned unchanged.
 *
 * @param file - Image file to optimize
 * @param onProgress - Optional progress callback (0-100)
 * @returns Optimization result with optimized file
 *
 * @example
 * ```typescript
 * const result = await optimizeForAnthropicApi(file);
 * if (result.wasOptimized) {
 *   console.log(`Resized from ${result.originalDimensions?.width}x${result.originalDimensions?.height}`);
 *   console.log(`to ${result.finalDimensions?.width}x${result.finalDimensions?.height}`);
 * }
 * ```
 */
export async function optimizeForAnthropicApi(
  file: File,
  onProgress?: CompressionProgressCallback,
): Promise<OptimizationResult> {
  const originalSize = file.size;

  // Get original dimensions
  let originalDimensions: { width: number; height: number } | undefined;
  try {
    originalDimensions = await getImageDimensions(file);
  } catch {
    // Continue without dimensions if we can't get them
  }

  // Check if optimization is needed
  const needsOptim = await needsOptimization(file);

  if (!needsOptim) {
    // File is already within limits
    return {
      file,
      wasOptimized: false,
      originalSize,
      finalSize: originalSize,
      originalDimensions,
      finalDimensions: originalDimensions,
    };
  }

  try {
    // Optimize with Anthropic-specific settings
    const optimizedFile = await imageCompression(file, {
      maxWidthOrHeight: ANTHROPIC_IMAGE_CONFIG.MAX_DIMENSION,
      maxSizeMB: ANTHROPIC_IMAGE_CONFIG.MAX_FILE_SIZE_MB,
      initialQuality: ANTHROPIC_IMAGE_CONFIG.QUALITY,
      useWebWorker: true,
      preserveExif: false, // Remove EXIF to reduce size
      onProgress: onProgress
        ? (progress: number) => onProgress(Math.round(progress))
        : undefined,
    });

    // Get final dimensions
    let finalDimensions: { width: number; height: number } | undefined;
    try {
      finalDimensions = await getImageDimensions(optimizedFile);
    } catch {
      // Continue without dimensions if we can't get them
    }

    return {
      file: optimizedFile,
      wasOptimized: true,
      originalSize,
      finalSize: optimizedFile.size,
      originalDimensions,
      finalDimensions,
    };
  } catch (error) {
    // If optimization fails, return original file with warning
    console.warn("Image optimization failed, using original:", error);
    return {
      file,
      wasOptimized: false,
      originalSize,
      finalSize: originalSize,
      originalDimensions,
      finalDimensions: originalDimensions,
    };
  }
}
